<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { adapter } from '@/services/prototype-adapter'
import { MENU_PAGES } from '@/data/registry'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import AppDrawer from '@/components/ui/AppDrawer.vue'
import AppIcon from '@/components/AppIcon.vue'
import { formatDateTime } from '@/utils/format'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()
const ui = useUiStore()
const auth = useAuthStore()
const route = useRoute()
const query = ref('')
const faq = computed(() => adapter.table('faq').filter(row => row.status === 'published').slice(0, 10))
const pages = computed(() => [...MENU_PAGES]
  .sort((left, right) => Number(right.route.replace(/^#/, '') === route.path) - Number(left.route.replace(/^#/, '') === route.path))
  .filter(page => auth.has(page.permission))
  .filter(page => !query.value || `${page.title}${page.purpose}`.includes(query.value))
  .slice(0, 8))

function copy(path: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    void navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${path}`)
    ui.toast('success', '页面地址已复制', '可直接分享当前原型深链。')
  }
}
</script>

<template>
  <AppDrawer :open="open" title="帮助中心" subtitle="页面入口、常见问题与原型操作说明" width="480px" @close="emit('close')">
    <div class="search-field">
      <AppIcon name="search" :size="16" class="search-field__icon" />
      <el-input v-model="query" placeholder="搜索页面或问题" clearable aria-label="搜索帮助" />
    </div>
    <section class="help-section">
      <h3 class="help-section__title">页面入口</h3>
      <button v-for="page in pages" :key="page.id" type="button" class="help-link" @click="copy(page.route)">
        <span><b>{{ page.title }}</b><small>{{ page.purpose }}</small></span>
        <span class="help-link__sub">{{ page.route.replace(/^#/, '') }}</span>
      </button>
    </section>
    <section class="help-section">
      <h3 class="help-section__title">常见问题</h3>
      <details v-for="item in faq" :key="String(item.id)" class="faq-item">
        <summary>{{ item.question }}</summary>
        <p>{{ item.answer }}</p>
        <small>来源：{{ item.category_name }} · 更新时间 {{ formatDateTime(item.updated_at) }}</small>
      </details>
    </section>
    <section class="help-section">
      <h3 class="help-section__title">原型操作提示</h3>
      <ul class="help-tips">
        <li>所有写操作只修改当前浏览器内存，刷新页面后恢复种子数据。</li>
        <li>右上角“故障注入”可演示超时、401、403、404、409、422、429、500 与断网。</li>
        <li>表格筛选、页码与滚动位置在当前会话内保留，可从工作台标签恢复关闭页面。</li>
      </ul>
    </section>
  </AppDrawer>
</template>
